import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import {
  BASE_CLASSES,
  ButtonRebrandVariant,
  VARIANT_CLASSES
} from 'src/components/atoms/ButtonRebrand/styles'
import { ExternalLinkComponent } from 'src/components/atoms/Link'

import { CtaButton, InternalLink } from 'src/config/generated/sanitySchema'
import { useMessenger } from 'src/hooks/useMessenger'
import { interpolatePathTokens } from 'src/utils/interpolatePathTokens'

export interface CmsCtaButtonRebrandProps
  extends Omit<CtaButton, '_type' | 'internalLink' | 'colorVariant'> {
  internalLink: InternalLink | undefined
  colorVariant?: ButtonRebrandVariant
  className?: string
}

export const CmsCtaButtonRebrand = ({
  text,
  externalUrl,
  internalLink,
  linkType,
  actionType,
  colorVariant = 'lime',
  className = ''
}: CmsCtaButtonRebrandProps) => {
  const { showNewMessage } = useMessenger()
  const { query } = useRouter()

  // Internal links may carry `{token}` placeholders (e.g.
  // `/get-started?tier={tier}&frequency={frequency}`) resolved from the current
  // route/query so one CMS link adapts per page.
  const internalHref = useMemo(
    () =>
      internalLink?.path
        ? interpolatePathTokens(internalLink.path, query)
        : undefined,
    [internalLink?.path, query]
  )

  const handleOnClick = useCallback(() => {
    if (linkType === 'action') {
      switch (actionType) {
        case 'messenger':
          showNewMessage()
          break
        default:
          break
      }
    }
  }, [actionType, linkType, showNewMessage])

  const linkClassName = useMemo(
    () => `${BASE_CLASSES} ${VARIANT_CLASSES[colorVariant]} ${className}`,
    [colorVariant, className]
  )

  if (linkType === 'action') {
    return (
      <ButtonRebrand
        variant={colorVariant}
        onClick={handleOnClick}
        className={className}
      >
        {text}
      </ButtonRebrand>
    )
  }

  if (linkType === 'internal' && internalHref) {
    return (
      <Link href={internalHref} className={linkClassName}>
        {text}
      </Link>
    )
  }

  if (linkType === 'external' && externalUrl) {
    return (
      <ExternalLinkComponent href={externalUrl} className={linkClassName}>
        {text}
      </ExternalLinkComponent>
    )
  }

  return null
}
